import sortBy from 'lodash/sortBy';
import { computed, ref, watch } from 'vue';
import { MainPagePopup } from '@/interface/enum';
import { useBetBuilderTutorialPopup } from '@/libraries/composables/popup/useBetBuilderTutorialPopup';
import { useJackpotWinnerPopup } from '@/libraries/composables/popup/useJackpotWinnerPopup';
import { useSingleModeBetSlipTutorialState } from '@/libraries/composables/popup/useSingleModeBetSlipTutorialState';
import { useTelegramBotPopup } from '@/libraries/composables/popup/useTelegramBotPopup';
import { useVoucherRedeemPopup } from '@/libraries/composables/popup/useVoucherRedeemPopup';

type PopupSlot = 'jackpotWin' | 'announcement';

interface PopupPlacement {
    slot: PopupSlot;
    order: number;
}
const PLACEMENT: Record<MainPagePopup, PopupPlacement> = {
    [MainPagePopup.JackpotWinner]: { slot: 'jackpotWin', order: 0 },
    [MainPagePopup.TelegramBot]: { slot: 'announcement', order: 0 },
    [MainPagePopup.VoucherRedeem]: { slot: 'announcement', order: 1 },
    [MainPagePopup.SingleModeBetSlipTutorial]: { slot: 'announcement', order: 2 },
    [MainPagePopup.BetBuilderTutorial]: { slot: 'announcement', order: 3 },
};

const announcementPopups = Object.values(MainPagePopup).filter(id => PLACEMENT[id].slot === 'announcement');

const ANNOUNCEMENT_PRIORITY: readonly MainPagePopup[] = sortBy(announcementPopups, id => PLACEMENT[id].order);

// Only the announcement slot needs a spent flag: closing one announcement retires the rest for
// the visit. The jackpot win has no such rule — its own composable retires the win it just
// claimed, and a later win is allowed back on screen.
const isAnnouncementSpent = ref(false);
const activePopup = ref<MainPagePopup | null>(null);

export function usePopupPriority() {
    const telegramBot = useTelegramBotPopup();
    const voucherRedeem = useVoucherRedeemPopup();
    const betBuilderTutorial = useBetBuilderTutorialPopup();
    const singleModeBetSlipTutorial = useSingleModeBetSlipTutorialState();
    const jackpotWinner = useJackpotWinnerPopup();

    const candidates: Record<MainPagePopup, () => boolean> = {
        [MainPagePopup.TelegramBot]: () => telegramBot.isShowPopup.value,
        [MainPagePopup.JackpotWinner]: () => jackpotWinner.isShowPopup.value,
        [MainPagePopup.SingleModeBetSlipTutorial]: () => singleModeBetSlipTutorial.isShowPopup.value,
        [MainPagePopup.BetBuilderTutorial]: () => betBuilderTutorial.isShowPopup.value,
        [MainPagePopup.VoucherRedeem]: () => voucherRedeem.isShowPopup.value,
    };

    const closeHandlers: Partial<Record<MainPagePopup, () => void | Promise<void>>> = {
        [MainPagePopup.TelegramBot]: telegramBot.closePopup,
        [MainPagePopup.JackpotWinner]: jackpotWinner.closePopup,
        [MainPagePopup.SingleModeBetSlipTutorial]: singleModeBetSlipTutorial.closePopup,
        [MainPagePopup.BetBuilderTutorial]: betBuilderTutorial.closePopup,
        [MainPagePopup.VoucherRedeem]: voucherRedeem.closePopup,
    };

    const announcement = computed<MainPagePopup | null>(() => (isAnnouncementSpent.value
        ? null
        : ANNOUNCEMENT_PRIORITY.find(id => candidates[id]()) ?? null));

    const jackpotWin = computed<MainPagePopup | null>(() => (
        candidates[MainPagePopup.JackpotWinner]() ? MainPagePopup.JackpotWinner : null));

    watch([announcement, jackpotWin], ([nextAnnouncement, nextJackpotWin]) => {
        if (activePopup.value === MainPagePopup.JackpotWinner && nextJackpotWin) {
            return;
        }

        if (!activePopup.value && nextJackpotWin) {
            activePopup.value = nextJackpotWin;
            return;
        }

        activePopup.value = nextAnnouncement ?? nextJackpotWin;
    }, { immediate: true });

    function closePopup(id: MainPagePopup) {
        if (PLACEMENT[id].slot === 'announcement') {
            isAnnouncementSpent.value = true;
        }

        closeHandlers[id]?.();

        activePopup.value = announcement.value ?? jackpotWin.value;
    }

    return {
        activePopup,
        closePopup,
    };
}
